import type { LeadDetail } from '@performance-crm/shared';
import { Paperclip } from 'lucide-react';
import { AttachmentsPanel, type AttachmentTarget } from '@/components/attachments/AttachmentsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLeadAttachments } from '@/lib/queries';

/** Archivos del lead, de su cliente (compartidos entre sus leads) y de sus separaciones y ventas. */
export function AttachmentsCard({ lead, canUpload }: { lead: LeadDetail; canUpload: boolean }) {
  const { data, isLoading, refetch } = useLeadAttachments(lead.id);
  const targets: AttachmentTarget[] = [
    { entityType: 'LEAD', entityId: lead.id, label: 'Este lead', badge: 'Lead' },
    {
      entityType: 'CONTACT',
      entityId: lead.contact.id,
      label: 'Cliente (todos sus leads)',
      badge: 'Cliente',
    },
    ...lead.deals
      .filter((d) => d.state !== 'ANULADO')
      .map((d) => {
        const label = `${d.type === 'VENTA' ? 'Venta' : 'Separación'} · ${
          d.units.map((u) => u.code).join(', ') || 'sin unidad'
        }`;
        return { entityType: 'DEAL' as const, entityId: d.id, label, badge: label };
      }),
  ];
  const count = data?.items.length ?? 0;
  return (
    <Card data-testid="attachments-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Paperclip className="h-4 w-4" /> Archivos
          {count > 0 && (
            <span className="text-muted-foreground text-xs font-normal">({count})</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <AttachmentsPanel
          items={data?.items}
          isLoading={isLoading}
          targets={targets}
          canUpload={canUpload}
          showTarget
          refresh={async () => (await refetch()).data?.items}
        />
      </CardContent>
    </Card>
  );
}
